import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ServerInferRequest } from "@ts-rest/core";
import { billcomContract } from "@common/contracts/billcom.contract";
import { IntegrationConnection } from "@common/types/integrationConnection";
import { integrationConnectionWithAccountsResponseSchema } from "@common/schemas/domains/integrationConnection/integration-connection-response.schema";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { SUPPRESS_ERROR_TOAST_HEADER } from "@lib/backendClient";
import { useConnectAccount } from "@components/connectAccount/ConnectAccount.context";
import { useConnectTarget } from "@hooks/useConnectTarget";
import { addIntegrationConnectionInQueryData } from "@queries/entitiesAccounts";
import { integrationConnectionQueryKeys } from "./integrationConnection.api";

const billcomContractClient = getBackendAPIClient(billcomContract);

export function useBillcomConnectionCreationMutation(
    onSuccess?: (connection: IntegrationConnection) => void,
) {
    const { workspaceId, isOrgConnect, refreshConnections } =
        useConnectTarget();
    const { updateConnectAccountState } = useConnectAccount();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (
            data: Omit<
                ServerInferRequest<
                    typeof billcomContract.createConnection
                >["body"],
                "workspaceId"
            >,
        ) => {
            if (!workspaceId && !isOrgConnect) {
                throw new Error("No active workspace");
            }

            const response = await billcomContractClient.createConnection({
                body: {
                    ...data,
                    workspaceId,
                },
                extraHeaders: {
                    [SUPPRESS_ERROR_TOAST_HEADER]: "true",
                },
            });

            if (response.status !== 201) {
                const errorBody = response.body;
                const errorMessage =
                    typeof errorBody === "object" &&
                    errorBody !== null &&
                    "message" in errorBody &&
                    typeof errorBody.message === "string"
                        ? errorBody.message
                        : "Failed to connect to Bill.com";
                throw new Error(errorMessage);
            }

            return integrationConnectionWithAccountsResponseSchema.parse(
                response.body,
            );
        },
        onMutate: () => {
            updateConnectAccountState({
                isConnecting: true,
            });
        },
        onSuccess: async (connection) => {
            addIntegrationConnectionInQueryData(queryClient, connection);
            const hasAccountsNotReadyForSync =
                connection.integrationAccounts?.some(
                    (a) => !a.financialAccount?.entityId,
                );

            if (hasAccountsNotReadyForSync) {
                updateConnectAccountState({
                    isConnecting: false,
                    connectionSaved: true,
                    integrationConnection: connection,
                });
            } else {
                updateConnectAccountState({
                    isConnecting: false,
                });
            }

            queryClient.invalidateQueries({
                queryKey: integrationConnectionQueryKeys.query(
                    workspaceId ?? "",
                ),
            });
            await refreshConnections({
                provider: "billcom",
                connectionId: String(connection.id),
            });
            onSuccess?.(connection);
        },
        onError: () => {
            updateConnectAccountState({
                isConnecting: false,
            });
        },
    });
}
